/**
 * StepModal component for the Indigo design system.
 *
 * A modal for a flow that takes several steps: a header (title, subtitle,
 * close), a `Stepper` to move between the steps, the active step's content,
 * and a footer with a summary on the left and the step's actions on the
 * right. The header, stepper and footer stay put; the content scrolls.
 *
 * The modal owns layout only. The consumer owns the flow: which step is
 * active, what each step renders, and which actions the footer offers for it
 * (Cancel / Back / Next, or a single Done on a final screen).
 *
 * Leave `steps` out for a flow whose navigation lives elsewhere (e.g. a
 * `NavList` in `startPane`): the stepper is then not rendered.
 *
 * Optional side panes split the content area:
 * - `startPane` — a shaded column on the left, for a master list whose
 *   selection drives the main content (pair it with `NavList`).
 * - `endPane` — a column on the right, for a preview or details of something
 *   picked in the main content (head it with `PanelHeader`).
 * Below the `md` breakpoint the panes stack above / below the main content.
 *
 * Clicking the mask does not close the modal by default: losing a half-done
 * flow to a stray click costs more than the extra press on Cancel.
 *
 * @example
 * ```tsx
 * <StepModal
 *   open={open}
 *   onClose={close}
 *   title="Share document"
 *   subtitle="Quarterly report · Due Apr 17"
 *   steps={[
 *     { id: "contents", label: "Contents" },
 *     { id: "people", label: "People" },
 *     { id: "review", label: "Review" },
 *   ]}
 *   activeStepId={step}
 *   onStepChange={setStep}
 *   footerStart="3 documents selected"
 *   footerActions={
 *     <>
 *       <Button variant="ghost" onClick={close}>Cancel</Button>
 *       <Button onClick={next}>Next</Button>
 *     </>
 *   }
 * >
 *   <ContentsStep />
 * </StepModal>
 * ```
 */
import { clsx } from "clsx";
import type { ReactNode } from "react";
import { Modal } from "../Modal";
import { Stepper, type StepperStep } from "../Stepper";
import { Body } from "../Typography/Body";
import { Heading } from "../Typography/Heading";
import "./StepModal.css";

export type StepModalStep = StepperStep;

export interface StepModalProps {
  /** Whether the modal is shown. */
  open: boolean;

  /** Called by the close button, Escape and (if enabled) the mask. */
  onClose: () => void;

  /** Modal title. */
  title: ReactNode;

  /** Context line under the title (what the flow is about). */
  subtitle?: ReactNode;

  /** Steps of the flow, in order. Leave out to render no stepper. */
  steps?: StepModalStep[];

  /** Id of the step being shown. Required with `steps`. */
  activeStepId?: string;

  /**
   * Called when a step is picked in the stepper. Leave it out to make the
   * stepper a progress indicator only (navigation through the footer).
   */
  onStepChange?: (stepId: string) => void;

  /**
   * Greys the stepper out and blocks navigation — for a final screen shown
   * after the flow has been submitted.
   */
  stepsDisabled?: boolean;

  /** Content of the active step. */
  children: ReactNode;

  /** Shaded column left of the content (e.g. a `NavList`). */
  startPane?: ReactNode;

  /** Column right of the content (e.g. a preview with a `PanelHeader`). */
  endPane?: ReactNode;

  /** Footer text on the left — a summary of what the flow will do. */
  footerStart?: ReactNode;

  /** Footer actions on the right, in reading order (Cancel, Back, Next). */
  footerActions?: ReactNode;

  /** Modal width in pixels. Capped to the viewport on small screens. @default 960 */
  width?: number;

  /** Whether clicking the mask closes the modal. @default false */
  maskClosable?: boolean;

  /**
   * Whether close, Escape and (if enabled) mask clicks can dismiss the modal.
   * Turn off while a step is saving. @default true
   */
  dismissible?: boolean;

  /** Accessible name of the stepper. @default "Steps" */
  stepsLabel?: string;

  /** Additional CSS classes applied to the modal root. */
  className?: string;

  /** Test id applied to the dialog wrapper. */
  "data-testid"?: string;
}

export const StepModal = ({
  open,
  onClose,
  title,
  subtitle,
  steps,
  activeStepId,
  onStepChange,
  stepsDisabled = false,
  children,
  startPane,
  endPane,
  footerStart,
  footerActions,
  width = 960,
  maskClosable = false,
  dismissible = true,
  stepsLabel,
  className,
  "data-testid": dataTestId,
}: StepModalProps) => {
  const hasFooter = Boolean(footerStart || footerActions);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={width}
      centered
      closable={dismissible}
      keyboard={dismissible}
      maskClosable={dismissible && maskClosable}
      rootClassName={clsx("indigo-step-modal", className)}
      classNames={{
        content: "indigo-step-modal__content",
        header: "indigo-step-modal__header",
        body: "indigo-step-modal__body",
        footer: "indigo-step-modal__footer",
      }}
      // Rendered in antd's title slot so the dialog is labelled by it.
      title={
        <div className="indigo-step-modal__title-block">
          <Heading level={2} className="indigo-step-modal__title">
            {title}
          </Heading>
          {subtitle && (
            <Body
              size="xs"
              className="indigo-step-modal__subtitle text-indigo-text-tertiary"
            >
              {subtitle}
            </Body>
          )}
        </div>
      }
      footer={
        hasFooter ? (
          <div className="indigo-step-modal__footer-row">
            <div className="indigo-step-modal__footer-start">{footerStart}</div>
            {footerActions && (
              <div className="indigo-step-modal__footer-actions">
                {footerActions}
              </div>
            )}
          </div>
        ) : null
      }
      // antd does not forward arbitrary props; the dialog wrapper does.
      wrapProps={dataTestId ? { "data-testid": dataTestId } : undefined}
    >
      {steps && steps.length > 0 && (
        <Stepper
          steps={steps}
          activeStepId={activeStepId ?? steps[0].id}
          onStepChange={onStepChange}
          disabled={stepsDisabled}
          ariaLabel={stepsLabel}
          className="indigo-step-modal__stepper"
        />
      )}
      <div className="indigo-step-modal__panes">
        {startPane && (
          <div className="indigo-step-modal__start-pane">{startPane}</div>
        )}
        <div className="indigo-step-modal__main">{children}</div>
        {endPane && (
          <div className="indigo-step-modal__end-pane">{endPane}</div>
        )}
      </div>
    </Modal>
  );
};

StepModal.displayName = "StepModal";
